/**
 * "Enter as Employee, or as <your role>?"
 *
 * Blocking by decision (4b): until it is answered the server refuses every
 * route with 403 LOGIN_MODE_REQUIRED, so there is nothing behind this dialog to
 * show anyway. There is no close button and Escape does nothing - the only ways
 * out are choosing, or signing out.
 *
 * Stepping up into role mode costs a password for HR Manager, HR Admin, IT INF
 * and Super Admin (1c). Only Manager and HR Assistant may have the choice
 * remembered on a device (5c) - the checkbox is not rendered for anyone else.
 */
import React, { useEffect, useState } from 'react';
import {
  Briefcase,
  LogOut,
  ShieldCheck,
  Loader2,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

export const ROLE_LABELS = {
  manager: { en: 'Manager', ar: 'مدير' },
  hr_assistant: { en: 'HR Assistant', ar: 'مساعد الموارد البشرية' },
  hr_manager: { en: 'HR Manager', ar: 'مدير الموارد البشرية' },
  hr_admin: { en: 'HR Admin', ar: 'إداري الموارد البشرية' },
  it_inf: { en: 'IT INF', ar: 'تقنية المعلومات' },
  super_admin: { en: 'Super Admin', ar: 'المشرف العام' },
};

export const roleLabel = (role, isRTL) =>
  ROLE_LABELS[role]?.[isRTL ? 'ar' : 'en']
  || (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ModeChooserDialog = () => {
  const {
    user, actualRole, modeStepUpRequired, modeCanBeRemembered,
    selectMode, rememberedMode, logout,
  } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [askingPassword, setAskingPassword] = useState(false);

  // Remembered on this device, so answer it for them and let them straight in.
  // The server re-checks that this role is allowed a remembered answer.
  useEffect(() => {
    const saved = rememberedMode(user?.id);
    if (saved && modeCanBeRemembered && !modeStepUpRequired) {
      setBusy(saved);
      selectMode(saved, { remember: true }).then((result) => {
        if (!result.success) setBusy(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (mode) => {
    if (mode === 'role' && modeStepUpRequired && !askingPassword) {
      setAskingPassword(true);
      return;
    }
    setBusy(mode);
    setError(null);
    const result = await selectMode(mode, { password, remember });
    if (!result.success) {
      setError(result.error);
      setBusy(null);
      setPassword('');
    }
  };

  const label = roleLabel(actualRole, isRTL);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="mode-chooser-dialog"
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl sm:p-8">
        <p className="text-sm text-slate-500">
          {isRTL ? `مرحباً ${user?.first_name || ''}` : `Welcome back, ${user?.first_name || ''}`}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 sm:text-3xl">
          {isRTL ? 'كيف تريد الدخول؟' : 'How would you like to enter?'}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {isRTL
            ? 'يمكنك تغيير ذلك في أي وقت من أعلى الشاشة.'
            : 'You can change this at any time from the top of the screen.'}
        </p>

        {!askingPassword ? (
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => choose('employee')}
              disabled={!!busy}
              className="group flex w-full items-start gap-4 rounded-lg border border-slate-200 p-4 text-start transition-colors hover:border-slate-900 hover:bg-slate-50 disabled:opacity-60"
              data-testid="mode-choose-employee"
            >
              <span className="mt-0.5 rounded-md bg-slate-100 p-2 text-slate-700 transition-colors group-hover:bg-slate-900 group-hover:text-white">
                {busy === 'employee'
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : <Briefcase className="h-5 w-5" />}
              </span>
              <span>
                <span className="block font-medium text-slate-900">
                  {isRTL ? 'الدخول كموظف' : 'Enter as Employee'}
                </span>
                <span className="mt-0.5 block text-sm text-slate-600">
                  {isRTL
                    ? 'ملفك، إجازاتك، خطاباتك ومستنداتك فقط. جميع الصلاحيات الإدارية مُعطّلة.'
                    : 'Your own profile, leave, letters and documents. Every elevated authority switched off.'}
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => choose('role')}
              disabled={!!busy}
              className="group flex w-full items-start gap-4 rounded-lg border border-slate-200 p-4 text-start transition-colors hover:border-slate-900 hover:bg-slate-50 disabled:opacity-60"
              data-testid="mode-choose-role"
            >
              <span className="mt-0.5 rounded-md bg-slate-100 p-2 text-slate-700 transition-colors group-hover:bg-slate-900 group-hover:text-white">
                {busy === 'role'
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : <ShieldCheck className="h-5 w-5" />}
              </span>
              <span>
                <span className="block font-medium text-slate-900">
                  {isRTL ? `الدخول كـ ${label}` : `Enter as ${label}`}
                </span>
                <span className="mt-0.5 block text-sm text-slate-600">
                  {isRTL
                    ? 'الواجهة الكاملة لدورك.'
                    : 'The full interface for your role.'}
                  {modeStepUpRequired && (
                    <span className="ms-1 inline-flex items-center gap-1 text-slate-500">
                      <Lock className="h-3 w-3" />
                      {isRTL ? 'يتطلب كلمة المرور' : 'asks for your password'}
                    </span>
                  )}
                </span>
              </span>
            </button>

            {modeCanBeRemembered && (
              <div
                role="checkbox"
                aria-checked={remember}
                tabIndex={0}
                onClick={() => setRemember((value) => !value)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setRemember((value) => !value);
                  }
                }}
                className="flex cursor-pointer items-center gap-2 pt-1 text-sm text-slate-600"
                data-testid="mode-remember-row"
              >
                <Checkbox
                  checked={remember}
                  tabIndex={-1}
                  className="pointer-events-none"
                  data-testid="mode-remember-checkbox"
                />
                {isRTL ? 'تذكّر اختياري على هذا الجهاز' : 'Remember my choice on this device'}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-slate-700">
              {isRTL
                ? `أدخل كلمة المرور للمتابعة كـ ${label}.`
                : `Enter your password to continue as ${label}.`}
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="mode-password">{isRTL ? 'كلمة المرور' : 'Password'}</Label>
              <Input
                id="mode-password"
                type="password"
                value={password}
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && password) choose('role'); }}
                data-testid="mode-password-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={() => choose('role')}
                disabled={!password || busy === 'role'}
                className="flex-1"
                data-testid="mode-password-submit"
              >
                {busy === 'role' && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {isRTL ? `متابعة كـ ${label}` : `Continue as ${label}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setAskingPassword(false); setPassword(''); setError(null); }}
                data-testid="mode-password-back"
              >
                {isRTL ? 'رجوع' : 'Back'}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
             data-testid="mode-chooser-error">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={logout}
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-slate-900"
          data-testid="mode-chooser-logout"
        >
          <LogOut className="h-3.5 w-3.5" />
          {isRTL ? 'تسجيل الخروج' : 'Sign out instead'}
        </button>
      </div>
    </div>
  );
};

export default ModeChooserDialog;
